import { NETWORK_ERROR, ApiError } from '@obv/api-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, type Query } from '@tanstack/react-query';

// Bump when cached response shapes change so stale persisted caches are discarded.
export const CACHE_VERSION = 'obv-cache-v2';

export const isNetworkError = (e: unknown) => e instanceof ApiError && e.code === NETWORK_ERROR;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      cacheTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: (count, err) => (err instanceof ApiError && err.status > 0 && err.status < 500 ? false : count < 3),
    },
    mutations: {
      // Keep retrying through flaky Wi-Fi; the Idempotency-Key in the variables makes it safe.
      retry: (count, err) => isNetworkError(err) && count < 30,
      retryDelay: (n) => Math.min(30_000, 1000 * Math.pow(2, n)),
    },
  },
});

function safeStorage(): Storage | undefined {
  try {
    const k = '__obv_probe';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export const persister = createSyncStoragePersister({ storage: safeStorage(), key: 'obv-query-cache', throttleTime: 2000 });

/**
 * What goes into localStorage (about 5 MB on the iPad, shared with everything else). Bulky
 * reference content — Quran pages, the kanji list — opts out with `meta: { persist: false }`
 * and is simply refetched.
 */
export function shouldPersist(q: Query): boolean {
  return q.state.status === 'success' && !(q.meta && q.meta.persist === false);
}
